import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, message, Card, Space, Tag,
  Popconfirm, ColorPicker
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../utils/axios';
import initManager from '../../utils/initManager';

const TagsSettings = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    // 检查是否已初始化，避免重复获取数据
    if (!initManager.isInitialized('tags')) {
      fetchTags();
    } else {
      // 使用缓存的数据
      const cachedData = initManager.getCachedData('tags');
      if (cachedData) {
        setTags(cachedData);
      } else {
        // 如果缓存为空，重新获取
        fetchTags();
      }
    }
  }, []);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tags');
      setTags(response.data);
      
      // 缓存数据并标记为已初始化
      initManager.setCachedData('tags', response.data);
      initManager.setInitialized('tags');
    } catch (error) {
      message.error('获取标签列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTag(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingTag(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/tags/${id}`);
      message.success('删除成功');
      fetchTags();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      // 确保颜色值是有效的十六进制格式
      let colorValue = values.color;
      if (colorValue && typeof colorValue === 'object' && colorValue.toHexString) {
        // 如果是从 ColorPicker 返回的对象，转换为十六进制字符串
        colorValue = colorValue.toHexString();
      } else if (colorValue && typeof colorValue === 'string') {
        // 如果是字符串，确保是有效的十六进制格式
        if (!/^#[0-9A-Fa-f]{6}$/.test(colorValue)) {
          message.error('颜色格式不正确，请选择有效的颜色');
          return;
        }
      }

      const submitData = {
        ...values,
        color: colorValue
      };

      if (editingTag) {
        await api.put(`/tags/${editingTag.id}`, submitData);
        message.success('更新成功');
      } else {
        await api.post('/tags', submitData);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchTags();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const columns = [
    {
      title: '标签名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Tag color={record.color}>{text}</Tag>
      )
    },
    {
      title: '颜色',
      dataIndex: 'color',
      key: 'color',
      render: (color) => (
        <div style={{ 
          width: 20, 
          height: 20, 
          backgroundColor: color, 
          borderRadius: 4,
          border: '1px solid #d9d9d9'
        }} />
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString('zh-CN')
    },
    {
      title: '操作',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Space size="small" style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个标签吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>标签管理</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          添加标签
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={tags}
        rowKey="id"
        loading={loading}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          pageSizeOptions: ['10', '20', '50', '100'],
          defaultPageSize: 20
        }}
      />

      <Modal
        title={editingTag ? '编辑标签' : '添加标签'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="标签名称"
            rules={[{ required: true, message: '请输入标签名称' }]}
          >
            <Input placeholder="请输入标签名称" />
          </Form.Item>

          <Form.Item
            name="color"
            label="标签颜色"
            initialValue="#007bff"
          >
            <ColorPicker />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingTag ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default TagsSettings; 